import { openDB } from 'idb';
import { uploadImage } from './imageUpload';
import { uploadDocument } from './docUpload';
import { v4 as uuidv4 } from 'uuid';
import { createSuccessToast } from './createToast';
import { removePendingToast, createPendingToast, removeErrorToasts } from './createOutboxToast';
import * as Sentry from '@sentry/react';

//
// DATABASE (OUTBOX)
//
const openOutboxDb = () =>
  openDB('upload-db', 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('outbox')) {
        db.createObjectStore('outbox', { keyPath: 'id' });
      }
    },
    terminated() {
      // O navegador fechou a conexão por fora do nosso controle (ex.: outra aba abrindo
      // a mesma base em versão diferente, ou o próprio browser encerrando conexões
      // ociosas). Sem isso, `dbPromise` continuava resolvendo para uma conexão morta e
      // qualquer transação seguinte quebrava com "InvalidStateError: ...database
      // connection is closing" — descartar o cache aqui faz a próxima chamada reabrir.
      dbPromise = openOutboxDb();
    },
  });

let dbPromise = openOutboxDb();

const outbox = {
  add: async item => {
    const db = await dbPromise;
    await db.put('outbox', item);
  },

  remove: async id => {
    const db = await dbPromise;
    await db.delete('outbox', id);
  },

  getAll: async () => {
    const db = await dbPromise;
    return db.getAll('outbox');
  },
  count: async () => {
    const db = await dbPromise;
    return db.count('outbox');
  },
};
const saveToOutbox = async ({ file, filePath, empresaId, type = 'photo' }) => {
  await outbox.add({
    id: uuidv4(),
    file,
    filePath,
    empresaId,
    type,
    retries: 0,
    createdAt: Date.now(),
    status: 'pending',
  });
};
let isProcessing = false;

// Lock global (Web Locks API) para coordenar o processamento entre abas: o IndexedDB
// é compartilhado por todas as abas da mesma origem, mas `isProcessing` acima só vale
// dentro da própria aba. Sem esse lock, duas abas podem pegar o mesmo item pendente e
// disparar dois uploads concorrentes do mesmo arquivo. Navegadores sem suporte a
// `navigator.locks` (ex.: Safari < 15.4) caem no comportamento anterior (só a guarda
// por aba), mantendo o risco de duplicidade entre abas nesses casos.
const runOutboxItems = async (cookie, setToastList) => {
  const db = await dbPromise;
  const items = await db.getAll('outbox');

  for (const item of items) {
    const now = Date.now();

    if (item.nextRetryAt && now < item.nextRetryAt) {
      continue;
    }

    try {
      item.status = 'uploading';
      await db.put('outbox', item);

      if (item.type === 'document') {
        await uploadDocument({
          file: item.file,
          filePath: item.filePath,
          token: cookie?.authToken,
        });
      } else {
        await uploadImage({
          file: item.file,
          filePath: item.filePath,
          token: cookie?.authToken,
        });
      }

      await db.delete('outbox', item.id);
      removeErrorToasts(setToastList);
      const successMsg =
        item.type === 'document' ? 'Documento enviado com sucesso.' : 'Foto do chamado enviada com sucesso.';
      createSuccessToast(setToastList, successMsg);
    } catch (err) {
      console.error('Erro ao reprocessar upload', err);

      item.retries = (item.retries || 0) + 1;

      const delayMs = Math.min(1000 * 2 ** item.retries, 60000);

      item.nextRetryAt = Date.now() + delayMs;
      item.status = 'pending';

      try {
        await db.put('outbox', item);
      } catch (putErr) {
        // Bug conhecido do Safari/iOS: gravar um Blob/File no IndexedDB pode falhar
        // ("UnknownError: Error preparing Blob/File data..."), inclusive ao tentar
        // regravar o próprio item aqui no retry. Sem este try/catch essa falha
        // escapava do loop como unhandled rejection e travava o processamento dos
        // itens seguintes da fila.
        console.error('Erro ao regravar item da outbox para retry', putErr);
        Sentry.captureException(putErr, {
          tags: { area: 'outbox', action: 'retry_put' },
          extra: { item_id: item.id, item_type: item.type },
        });
      }
    }
  }
};

const processOutbox = async (cookie, setToastList) => {
  if (isProcessing) return;
  if (!navigator.onLine) return;

  // Chamado no mount de OutboxProcessor (global, toda página autenticada), no evento
  // 'online' e a cada hora — nenhum desses chamadores tem `.catch`. Sem este try/catch,
  // qualquer falha inesperada de IndexedDB (ex.: "UnknownError: Internal error." em modo
  // privado/restrito, ou conexão fechada por fora) virava unhandled rejection em toda
  // página, não só nas telas que de fato usam upload/outbox.
  try {
    const total = await outbox.count();

    if (!total) {
      removePendingToast(setToastList);
      return;
    }

    createPendingToast(setToastList, 'Enviando arquivos em segundo plano…');
    isProcessing = true;

    try {
      if (navigator.locks?.request) {
        // `ifAvailable: true` faz a chamada retornar imediatamente com `lock === null`
        // quando outra aba já segura o lock, em vez de enfileirar e reprocessar os
        // mesmos itens depois que a outra aba já os enviou.
        await navigator.locks.request('leankeep-outbox-processing', { ifAvailable: true }, async lock => {
          if (!lock) return;
          await runOutboxItems(cookie, setToastList);
        });
      } else {
        await runOutboxItems(cookie, setToastList);
      }
    } finally {
      isProcessing = false;
      const remaining = await outbox.count();

      if (remaining === 0) {
        removePendingToast(setToastList);
      }
    }
  } catch (err) {
    isProcessing = false;
    console.error('Erro ao processar a outbox', err);
    Sentry.captureException(err, { tags: { area: 'outbox', action: 'process' } });
  }
};

export { saveToOutbox, processOutbox };
